import { FC } from "react";

import { Badge, BadgeProps, Box, Tooltip } from "@hightouch/htui";
import capitalize from "lodash/capitalize";

import { JourneyState, JourneyTemplateState } from "src/pages/journeys/types";

const STATUS_MAPPING: Record<
  JourneyState | JourneyTemplateState,
  {
    variant: BadgeProps["variant"];
    message: string;
    isPending?: boolean;
    label?: string;
  }
> = {
  active: { variant: "info", message: "Journey is currently running" },
  deleting: {
    variant: "subtle",
    label: "Deletion in progress",
    message: "Journey is being deleted",
    isPending: true,
  },
  draft: {
    variant: "ghost",
    message: "Journey that has not run yet",
    isPending: true,
  },
  draining: {
    variant: "info",
    message:
      "Not accepting new entries but existing members will complete the journey",
  },
  off: {
    variant: "subtle",
    message: "Journey is not accepting new entries and all members have exited",
  },
  paused: {
    variant: "warning",
    message:
      "Journey is not accepting new entries and existing members remain in their current step",
  },
  resetting: {
    variant: "subtle",
    message: "Journey is being reset",
  },
  staged: {
    variant: "info",
    message: "Journey is being edited in staging mode",
  },
  template: {
    variant: "info",
    message: "A template used for creating new journeys from",
  },
};

type Props = {
  status: JourneyState | JourneyTemplateState;
  size?: BadgeProps["size"];
};

export const JourneyStatusBadge: FC<Props> = ({ status, size }) => {
  const badge = STATUS_MAPPING[status];

  if (!badge) {
    return null;
  }

  return (
    <Tooltip message={badge.message}>
      <Box flexShrink={0}>
        <Badge isPending={badge.isPending} size={size} variant={badge.variant}>
          {badge.label ?? capitalize(status)}
        </Badge>
      </Box>
    </Tooltip>
  );
};
